import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../style/onboarding2.css";

export default function OnboardingStep2() {
  const navigate = useNavigate();
  const location = useLocation();

  const initial = useMemo(() => {
    const s = location.state || {};
    if (Object.keys(s).length) return s;
    try {
      const saved = localStorage.getItem("onboarding.brand");
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  }, [location.state]);

  const [businessName, setBusinessName] = useState(initial.businessName || "");
  const [businessDesc, setBusinessDesc] = useState(initial.description || "");

  // NEW: take candidates list; pick first that loads
  const [logoSrc, setLogoSrc] = useState(initial.logoPrimary || "");
  const [wmSrc, setWmSrc] = useState("");

  useEffect(() => {
    if (logoSrc) return; // already set (from primary)
    const list = initial.logoCandidates || [];
    if (!list.length) return;

    let cancelled = false;
    (async () => {
      for (const url of list) {
        try {
          await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = resolve;
            img.onerror = reject;
            img.src = url;
          });
          if (!cancelled) {
            setLogoSrc(url);
            break;
          }
        } catch {
          // try next
        }
      }
    })();

    return () => { cancelled = true; };
  }, [initial.logoCandidates, logoSrc]);

  const [colors, setColors] = useState(initial.brandColors || ["#ffffff", "#ffffff", "#ffffff"]);
  const [enabled, setEnabled] = useState([true, false, false]);

  const [openMenu, setOpenMenu] = useState(null); // 'logo' | 'wm' | null
  const menuRef = useRef(null);

  useEffect(() => {
    function outside(e) {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) setOpenMenu(null);
    }
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, []);

  function onImgError(setter) {
    return () => setter(""); // hide broken logo → show '+'
  }

  function onPickFile(e, setter) {
    const file = e.target.files?.[0];
    if (!file) return;
    const r = new FileReader();
    r.onload = ev => setter(ev.target?.result || "");
    r.readAsDataURL(file);
  }

  function applyColorToSlot(i, val) {
    setColors(prev => { const n=[...prev]; n[i]=val; return n; });
    setEnabled(prev => { const n=[...prev]; if (i+1<n.length) n[i+1]=true; return n; });
  }
  function removeAndShift(i) {
    setColors(prev => {
      const n=[...prev];
      for (let k=i; k<n.length-1; k++) n[k]=n[k+1];
      n[n.length-1]="#ffffff";
      return n;
    });
    setEnabled(prev => { const n=[...prev]; for (let k=i+1;k<n.length;k++) n[k]=false; n[0]=true; return n; });
  }

  function handleBack(){ navigate("/onboarding/step-1"); }
  function handleNext(){
  const payload = {
    businessName,
    description: businessDesc,
    favicon: logoSrc,
    brandColors: colors,
  };
  localStorage.setItem("onboarding.brand", JSON.stringify(payload));

  navigate("/onboarding/step-3", { state: payload }); // <-- go to step-3
}

  return (
    <div className="page-wrap">
      {/* LEFT gradient; ribbon now pinned to bottom via CSS */}
      <aside className="left-panel" aria-hidden="false">
        <div className="left-card">
          <h1 className="title">Review your<br/>business<br/>info</h1>
          <p className="subtitle">
            We've pulled your business description and branding straight from your
            website to create personalized blog posts. Review and update any details
            if needed, and you're all set!
          </p>
        </div>
      </aside>

      {/* RIGHT */}
      <main className="right-panel">
        <form className="form-area" onSubmit={(e)=>e.preventDefault()}>
          <label className="field-label">Business name:</label>
          <input
            className="input"
            placeholder="Your business name"
            type="text"
            value={businessName}
            onChange={(e)=>setBusinessName(e.target.value)}
          />

          <label className="field-label">
            Business description (will be used as a prompt for Clikke Creator)
          </label>
          <textarea
            className="textarea"
            placeholder="Describe your business so we create tailored blog posts for you…"
            value={businessDesc}
            onChange={(e)=>setBusinessDesc(e.target.value)}
          />

          <div className="branding-section">
            <div className="section-title">Branding:</div>

            <div className="branding-grid" ref={menuRef}>
              {/* LOGO */}
              <div className="brand-card">
                <div className="brand-left"><div className="brand-label">Logo</div></div>
                <div className="brand-right">
                  <div
                    className="placeholder large"
                    title="Click to add logo"
                    onClick={()=>document.getElementById("logoInput")?.click()}
                  >
                    {logoSrc ? (
                      <img src={logoSrc} alt="logo" className="preview-img" onError={onImgError(setLogoSrc)} />
                    ) : (
                      <div className="placeholder-dot">+</div>
                    )}
                  </div>

                  <input id="logoInput" type="file" accept="image/*" className="file-input"
                         onChange={e=>onPickFile(e,setLogoSrc)} />

                  {logoSrc && (
                    <div className="dropdown" data-for="logo">
                      <button type="button"
                        className="dropdown-toggle"
                        aria-expanded={openMenu==='logo'}
                        onClick={()=>setOpenMenu(openMenu==='logo'?null:'logo')}>
                        ⋯
                      </button>

                      {openMenu==='logo' && (
                        <div className="dropdown-menu" role="menu">
                          <button type="button" className="dropdown-item"
                                  onClick={()=>document.getElementById("logoInput")?.click()}>
                            Replace
                          </button>
                          <button type="button" className="dropdown-item danger"
                                  onClick={()=>{ setLogoSrc(''); setOpenMenu(null); }}>
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* WATERMARK */}
              <div className="brand-card">
                <div className="brand-left"><div className="brand-label">Watermark</div></div>
                <div className="brand-right">
                  <div className="placeholder large" title="Click to add watermark"
                       onClick={()=>document.getElementById("wmInput")?.click()}>
                    {wmSrc ? (
                      <img src={wmSrc} alt="watermark" className="preview-img" onError={onImgError(setWmSrc)} />
                    ) : (
                      <div className="placeholder-dot">+</div>
                    )}
                  </div>

                  <input id="wmInput" type="file" accept="image/*" className="file-input"
                         onChange={e=>onPickFile(e,setWmSrc)} />

                  {wmSrc && (
                    <div className="dropdown" data-for="wm">
                      <button type="button"
                        className="dropdown-toggle"
                        aria-expanded={openMenu==='wm'}
                        onClick={()=>setOpenMenu(openMenu==='wm'?null:'wm')}>
                        ⋯
                      </button>

                      {openMenu==='wm' && (
                        <div className="dropdown-menu" role="menu">
                          <button type="button" className="dropdown-item"
                                  onClick={()=>document.getElementById("wmInput")?.click()}>
                            Replace
                          </button>
                          <button type="button" className="dropdown-item danger"
                                  onClick={()=>{ setWmSrc(''); setOpenMenu(null); }}>
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* COLORS */}
              <div className="brand-card colors-card">
                <div className="brand-label">Colors</div>
                <div className="colors-controls">
                  <div className="colors-list">
                    {[0,1,2].map(idx=>{
                      const canUse = enabled[idx];
                      const val = colors[idx];
                      const filled = canUse && val !== "#ffffff";
                      return (
                        <div className="color-slot" key={idx}>
                          <div
                            className={[
                              "color-swatch",
                              filled ? "filled" : canUse ? "empty active" : "empty disabled-dashed"
                            ].join(" ")}
                            style={filled?{backgroundColor:val}:{}} 
                            onClick={()=>canUse && document.getElementById(`color-${idx}`)?.click()}
                            tabIndex={canUse?0:-1}
                          >
                            <input id={`color-${idx}`} className="color-input" type="color"
                                   value={val} disabled={!canUse}
                                   onChange={e=>applyColorToSlot(idx, e.target.value)} />
                          </div>
                          {filled && (
                            <button className="color-remove" onClick={(e)=>{e.preventDefault(); removeAndShift(idx);}}>
                              ✕
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="colors-note">Add the first color to unlock the next slots.</div>
                </div>
              </div>
            </div>
          </div>

          {/* Buttons — same look as Step-1 */}
          <div className="form-actions">
            <button type="button" className="btn outline-pill" onClick={handleBack}>Back</button>
            <button type="button" className="btn primary-pill" onClick={handleNext}>Next</button>
          </div>
        </form>
      </main>
    </div>
  );
}
